import axios from "axios";
import { accessTokenService } from "../services/access-token-service";

// In production the UI is served by the Static Web App and the API is a separate
// Container App, so requests must go cross-origin to VITE_API_URL (the API allows
// the UI origin via CORS). In local dev we keep the relative "/api" so the Vite
// dev-server proxy (see vite.config.ts) forwards to the local API.
const apiBaseUrl =
  import.meta.env.PROD && import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : "/api";

const apiClient = axios.create({
  baseURL: apiBaseUrl,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await accessTokenService.getAccessToken();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * An API failure, carrying the server's error code alongside the message. `code` is the key from a
 * ValidationProblem's `errors` map (the handler's ErrorCode), which lets callers branch on the kind of
 * failure rather than sniffing message text.
 *
 * `message` is always safe to show a customer. `serverDetail` is not — it's whatever the server sent
 * for diagnostics, which in Development is the full exception and stack trace. Log it, never render it.
 */
export interface ApiError extends Error {
  code?: string;
  status?: number;
  serverDetail?: string;
  /** The failed request's trace id, when the server sent one. Safe to show, unlike serverDetail: it
   *  identifies a trace rather than a customer, and it is the only handle support has on a fault the
   *  member cannot describe. */
  traceId?: string;
}

// What the customer sees when the failure is ours rather than theirs. Deliberately says nothing about
// what broke: a 500 body is written for developers, not for the person who just tried to pay.
const GENERIC_ERROR_MESSAGE =
  "Something went wrong at our end. Please try again, and contact support if it keeps happening.";

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    const status: number | undefined = error.response?.status;

    // ValidationProblem responses carry the specific message in `errors` (keyed by error code), while
    // `title` is only the generic "One or more validation errors occurred." Prefer the specific one.
    const [firstCode, firstMessages] =
      data?.errors && typeof data.errors === "object"
        ? (Object.entries(data.errors)[0] ?? [])
        : [];
    const firstValidationError = Array.isArray(firstMessages)
      ? (firstMessages[0] as string | undefined)
      : undefined;

    // 4xx bodies are authored for people — validation messages, "not found", "already subscribed" —
    // so they're worth showing. Anything else is a fault, not a message: ApiExceptionMiddleware puts
    // the entire exception and stack trace in `detail` outside production, and even in production the
    // title is boilerplate. Rendering either at someone mid-checkout is how a customer ends up
    // reading our file paths, so server faults and network failures always get our own wording.
    const isServerFault = status === undefined || status >= 500;

    const message = isServerFault
      ? GENERIC_ERROR_MESSAGE
      : (firstValidationError ??
        (typeof data?.detail === "string" ? data.detail : undefined) ??
        data?.title ??
        GENERIC_ERROR_MESSAGE);

    const apiError: ApiError = new Error(message);
    if (typeof firstCode === "string") {
      apiError.code = firstCode;
    }
    apiError.status = status;
    if (typeof data?.traceId === "string") {
      apiError.traceId = data.traceId;
    }
    // Kept off `message` so it can never reach the screen, but attached and logged so a developer
    // still gets the stack trace from the browser rather than having to go to the API logs.
    if (typeof data?.detail === "string") {
      apiError.serverDetail = data.detail;
    }
    if (isServerFault) {
      console.error(
        `API ${status ?? "network"} failure: ${error.config?.method?.toUpperCase() ?? ""} ${error.config?.url ?? ""}`,
        data?.detail ?? error.message
      );
    }

    return Promise.reject(apiError);
  }
);

export default apiClient;
